import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Mail, RefreshCw, ShieldX } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { API } from '@/App';

const getTokenFromLocation = (location) => {
  const params = new URLSearchParams(location.search || window.location.search || '');
  return String(params.get('token') || '').trim();
};

const buildTrackingPath = (trackingCode) => {
  const normalizedTrackingCode = String(trackingCode || '').trim().toUpperCase();
  return normalizedTrackingCode ? `/tracking?code=${encodeURIComponent(normalizedTrackingCode)}` : '/tracking';
};

const TrackingEmailActionPage = ({ mode = 'confirm' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [status, setStatus] = useState('pending');
  const [message, setMessage] = useState('');
  const [trackingCode, setTrackingCode] = useState('');
  const [canResend, setCanResend] = useState(false);

  const token = useMemo(() => getTokenFromLocation(location), [location]);
  const isConfirmMode = mode === 'confirm';

  useEffect(() => {
    let active = true;

    const runAction = async () => {
      if (!token) {
        if (active) {
          setStatus('invalid');
          setMessage(
            isConfirmMode
              ? 'Lien de confirmation invalide.'
              : 'Lien de desabonnement invalide.'
          );
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          isConfirmMode
            ? `${API}/tracking/email/confirm`
            : `${API}/tracking/email/unsubscribe`,
          {
            params: { token },
          }
        );
        if (!active) {
          return;
        }
        const payload = response.data?.data || {};
        setTrackingCode(payload.tracking_code || '');
        setCanResend(Boolean(payload.can_resend_confirmation));
        setStatus(payload.status || (isConfirmMode ? 'confirmed' : 'unsubscribed'));
        setMessage(
          response.data?.message
            || (isConfirmMode
              ? 'Le suivi email est maintenant confirme pour ce colis.'
              : 'Vous ne recevrez plus les mises a jour de ce colis.')
        );
      } catch (error) {
        if (!active) {
          return;
        }
        const payload = error.response?.data || {};
        setTrackingCode(payload.tracking_code || '');
        setCanResend(Boolean(payload.can_resend_confirmation));
        if (isConfirmMode && error.response?.status === 410) {
          setStatus('expired');
          setMessage(payload.detail || 'Lien de confirmation expire.');
        } else {
          setStatus('invalid');
          setMessage(
            payload.detail
              || (isConfirmMode
                ? 'Lien de confirmation invalide.'
                : 'Lien de desabonnement invalide.')
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    runAction();

    return () => {
      active = false;
    };
  }, [isConfirmMode, token]);

  const handleResend = async () => {
    if (!trackingCode) {
      return;
    }
    setResending(true);
    try {
      const response = await axios.post(`${API}/tracking/email/resend-confirmation`, {
        tracking_code: trackingCode,
      });
      const payload = response.data?.data || {};
      setStatus('pending_confirmation');
      setCanResend(Boolean(payload.can_resend_confirmation));
      setMessage(response.data?.message || 'Email de confirmation renvoye.');
    } catch (error) {
      setMessage(
        error.response?.data?.detail
          || "Impossible de renvoyer l'email de confirmation pour le moment."
      );
    } finally {
      setResending(false);
    }
  };

  const renderState = () => {
    if (loading) {
      return (
        <div className="py-10 text-center" data-testid="tracking-email-action-loading">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-red-100 border-t-red-500" />
          <p className="mt-4 text-sm text-slate-600">
            {isConfirmMode ? 'Verification en cours...' : 'Traitement du desabonnement...'}
          </p>
        </div>
      );
    }

    const successState = ['confirmed', 'already_confirmed', 'unsubscribed', 'already_unsubscribed'].includes(status);
    const pendingState = status === 'pending_confirmation';
    const errorState = status === 'expired' || status === 'invalid';
    const Icon = successState ? CheckCircle2 : pendingState ? Mail : errorState ? AlertTriangle : ShieldX;

    return (
      <div className="space-y-6" data-testid={`tracking-email-action-state-${status}`}>
        <div
          className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
            successState
              ? 'bg-emerald-100 text-emerald-700'
              : pendingState
                ? 'bg-cyan-100 text-cyan-700'
                : 'bg-rose-100 text-rose-700'
          }`}
        >
          <Icon className="h-8 w-8" />
        </div>

        <div className="space-y-2 text-center">
          <CardTitle className="text-2xl">
            {status === 'confirmed' && 'Suivi email confirme'}
            {status === 'already_confirmed' && 'Suivi deja confirme'}
            {status === 'pending_confirmation' && 'Confirmation en attente'}
            {status === 'expired' && 'Lien expire'}
            {status === 'unsubscribed' && 'Suivi email desactive'}
            {status === 'already_unsubscribed' && 'Suivi deja desactive'}
            {status === 'invalid' && 'Lien invalide'}
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            {message}
          </CardDescription>
          {trackingCode ? (
            <p className="text-sm text-slate-500" data-testid="tracking-email-action-code">
              Code de suivi : <span className="font-medium text-slate-700">{trackingCode}</span>
            </p>
          ) : null}
        </div>

        <div className="space-y-3">
          {isConfirmMode && (status === 'expired' || status === 'pending_confirmation') && canResend && trackingCode ? (
            <Button
              type="button"
              className="w-full bg-red-500 hover:bg-red-600"
              onClick={handleResend}
              disabled={resending}
              data-testid="tracking-email-action-resend"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Envoi...' : 'Renvoyer email'}
            </Button>
          ) : null}

          <Button
            type="button"
            className="w-full bg-cyan-600 hover:bg-cyan-700"
            onClick={() => navigate(buildTrackingPath(trackingCode))}
            data-testid="tracking-email-action-open-tracking"
          >
            <ArrowRight className="mr-2 h-4 w-4" />
            Suivre ce colis
          </Button>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => navigate('/')}
            data-testid="tracking-email-action-back-home"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour a l'accueil
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-cyan-50">
      <Header />

      <main className="mx-auto flex min-h-[calc(100vh-180px)] max-w-6xl items-center px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid w-full gap-8 lg:grid-cols-[1.2fr_0.8fr]">
          <section className="space-y-6">
            <div className="inline-flex items-center rounded-full border border-red-100 bg-white/80 px-4 py-2 text-sm font-medium text-red-700 shadow-sm">
              {isConfirmMode ? 'Confirmation du suivi email' : 'Desabonnement du suivi email'}
            </div>
            <div className="space-y-4">
              <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl">
                {isConfirmMode
                  ? 'Confirmez votre suivi email en toute securite'
                  : 'Desactivez proprement les mises a jour email'}
              </h1>
              <p className="max-w-2xl text-lg text-slate-600">
                {isConfirmMode
                  ? 'Cette page confirme de facon explicite votre abonnement aux notifications email pour un colis precis.'
                  : 'Cette page vous permet de stopper les mises a jour email d un colis sans affecter les autres communications Coli Noki.'}
              </p>
            </div>
          </section>

          <Card className="border-red-100 bg-white/90 shadow-xl shadow-red-100/40">
            <CardHeader>
              <CardTitle>
                {isConfirmMode ? 'Etat de votre confirmation' : 'Etat de votre desabonnement'}
              </CardTitle>
              <CardDescription>
                Aucun email n est affiche publiquement sur cette page.
              </CardDescription>
            </CardHeader>
            <CardContent>{renderState()}</CardContent>
          </Card>
        </div>
      </main>

      <FooterNew />
    </div>
  );
};

export default TrackingEmailActionPage;
